import { API_BASE_URL, getAuthToken } from '../garden/services/gardenApi';
import { convertHeicToJpeg, isHeicFile } from '../utils/convertHeic';
import { downscaleImageForPlantIdentification } from '../utils/imageCompression';

/** PlantNet organ values accepted by POST /api/identify-plant */
export const PLANT_IDENTIFY_ORGANS = ['leaf', 'flower', 'fruit', 'bark', 'habit', 'other'];

/**
 * Convert HEIC/HEIF to JPEG when needed, then downscale for mobile memory limits
 * and upload size (full-resolution camera images often OOM on Android before upload).
 */
export async function preparePlantIdentifyImageFile(file) {
  if (!file) return null;
  let out = file;
  if (isHeicFile(out)) {
    out = await convertHeicToJpeg(out, { quality: 0.78 });
  }
  try {
    out = await downscaleImageForPlantIdentification(out, 1600, 0.82);
  } catch (e) {
    console.warn('[plantIdentify] downscale failed, continuing with current file', e);
  }
  return out;
}

/**
 * Upload a single image to the same blob store used by PlantNet identify.
 * @returns {{ blobUrl: string, filename?: string }}
 */
export async function uploadPlantIdentificationImage(file) {
  const token = getAuthToken();
  if (!token) throw new Error('Please sign in to identify plants.');

  const prepared = await preparePlantIdentifyImageFile(file);
  const form = new FormData();
  form.append('image', prepared);

  const uploadResp = await fetch(`${API_BASE_URL}/blob/upload`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: form,
  });
  const uploadData = await uploadResp.json().catch(() => ({}));
  if (!uploadResp.ok) {
    throw new Error(uploadData.error || uploadData.message || 'Upload failed');
  }
  const blobUrl = uploadData.blobUrl;
  if (!blobUrl) throw new Error('Upload did not return blobUrl');
  return { blobUrl, filename: uploadData.filename };
}

/**
 * Call PlantNet-backed identify after the image is reachable at blobUrl.
 * @returns {Promise<object>} Server JSON (success, source, result, …)
 */
export async function identifyPlantWithBlobUrl(blobUrl, organ = 'leaf') {
  const token = getAuthToken();
  if (!token) throw new Error('Please sign in to identify plants.');

  const idResp = await fetch(`${API_BASE_URL}/identify-plant`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ blobUrl, organ: organ || 'leaf' }),
  });
  const idData = await idResp.json().catch(() => ({}));
  if (!idResp.ok) {
    const parts = [];
    if (idData.message) parts.push(idData.message);
    if (!idData.message && idData.error) parts.push(idData.error);
    if (idData.status) parts.push(`(PlantNet HTTP ${idData.status})`);
    if (idData.details && typeof idData.details === 'object') {
      const detailMsg = idData.details.message || idData.details.error || idData.details.title;
      if (detailMsg) parts.push(detailMsg);
    }
    if (idData.rawText) parts.push(idData.rawText);
    const msg = parts.filter(Boolean).join(' ') || `HTTP ${idResp.status}`;
    const err = new Error(msg);
    err.status = idResp.status;
    err.code = idData.error;
    throw err;
  }
  return idData;
}

/** Upload then identify in one step. */
export async function identifyPlantFromImageFile(file, organ = 'leaf') {
  const { blobUrl } = await uploadPlantIdentificationImage(file);
  return identifyPlantWithBlobUrl(blobUrl, organ);
}

/**
 * Normalize PlantNet results from /api/identify-plant response body.
 * @param {object} identifyJson — parsed JSON from identify endpoint
 * @returns {Array<{ score: number|null, scientificName: string, commonName: string, commonNames: string[] }>}
 */
export function mapPlantNetMatches(identifyJson) {
  const results = identifyJson?.result?.results;
  if (!Array.isArray(results)) return [];
  return results.map((r) => {
    const species = r.species || {};
    const scientificName =
      species.scientificNameWithoutAuthor || species.scientificName || '';
    const commonNames = Array.isArray(species.commonNames) ? species.commonNames : [];
    const commonName = commonNames[0] || '';
    return {
      score: typeof r.score === 'number' ? r.score : null,
      scientificName,
      commonName,
      commonNames,
    };
  });
}
